import { PRMarinaNode, PRRect } from '@sketch-hq/sketch-web-renderer'
import {
  GridSettings,
  LayoutSettings,
  SketchArtboardNode,
  SketchElementNode,
  SketchElementNodeBase,
  SketchLayerNode,
} from '../types'
import { getElementTypeFromPRMarinaLayer } from './getElementTypeFromPRMarinaLayer'

export function createSketchElementNode(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,
  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchElementNode {
  if (marinaNode.isArtboard()) {
    return createSketchArtboardNode(
      marinaNode,
      elementRenderingIndex,
      rootNodeBounds
    )
  }

  return createSketchLayerNode(
    marinaNode,
    elementRenderingIndex,
    rootNodeBounds
  )
}

export function createSketchArtboardNode(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,
  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchArtboardNode {
  const prMarinaLayer = marinaNode.getLayer()
  if (!prMarinaLayer) {
    throw new Error("Can't create a SketchArtboardNode without a PRMarinaLayer")
  }

  const elementType = getElementTypeFromPRMarinaLayer(prMarinaLayer)

  if (elementType !== 'artboard' && elementType !== 'symbolMaster') {
    throw new Error('The layer type must be an artboard or a symbol master')
  }

  return {
    type: elementType,
    ...getSketchElementBaseProperties(
      marinaNode,
      elementRenderingIndex,
      rootNodeBounds
    ),
    identifier: marinaNode.getIdentifier(),
    gridSettings: getGridSettings(marinaNode),
    layoutSettings: getLayoutSettings(marinaNode),
  }
}

export function createSketchLayerNode(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,
  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchLayerNode {
  const prMarinaLayer = marinaNode.getLayer()
  if (!prMarinaLayer) {
    throw new Error("Can't create a SketchArtboardNode without a PRMarinaLayer")
  }
  const elementType = getElementTypeFromPRMarinaLayer(prMarinaLayer)
  if (
    elementType === 'artboard' ||
    elementType === 'symbolMaster' ||
    elementType === 'page'
  ) {
    throw new Error('The element type must be a layer')
  }

  return {
    type: elementType,
    ...getSketchElementBaseProperties(
      marinaNode,
      elementRenderingIndex,
      rootNodeBounds
    ),
  }
}

export function getSketchElementBaseProperties(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,

  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchElementNodeBase {
  const prRectBounds = marinaNode.getAbsoluteBounds()

  return {
    prMarinaNode: marinaNode,
    identifier: marinaNode.getIdentifier(),
    name: marinaNode.getLayerName(),
    nodeUUID: marinaNode.getLayerUUID(),
    children: [],
    // We want the bounds to be relative to the artboard and not the canvas if the root of the tree is just an artboard.
    rootRelativeBounds: {
      x: prRectBounds.x - rootNodeBounds.x,
      y: prRectBounds.y - rootNodeBounds.y,
      width: prRectBounds.width,
      height: prRectBounds.height,
    },
    elementRenderingIndex,
  }
}

function getGridSettings(marinaNode: PRMarinaNode): GridSettings {
  const defaultGridSettings: GridSettings = {
    isEnabled: false,
    size: 20,
    thickLineStep: 10,
  }
  const prMarinaArtboard = marinaNode.getArtboard()
  const prArtboardGrid = prMarinaArtboard?.getGrid()

  const gridSettings: GridSettings | undefined = prArtboardGrid
    ? {
        isEnabled: prArtboardGrid.isEnabled,
        size: prArtboardGrid.size,
        thickLineStep: prArtboardGrid.thickGridTimes,
      }
    : undefined

  return {
    ...defaultGridSettings,
    ...gridSettings,
  }
}

function getLayoutSettings(marinaNode: PRMarinaNode): LayoutSettings {
  const prMarinaArtboard = marinaNode.getArtboard()
  const prArtboardLayout = prMarinaArtboard?.getLayout()
  const defaultLayoutSettings: LayoutSettings = {
    columnWidth: 60,
    drawHorizontal: false,
    drawHorizontalLines: false,
    drawVertical: true,
    gutterHeight: 12,
    gutterWidth: 20,
    guttersOutside: true,
    horizontalOffset: 0,
    isEnabled: false,
    numberOfColumns: 5,
    numberOfRows: 0,
    rowHeightMultiplication: 5,
    totalWidth: 0,
  }

  const layoutSettings: LayoutSettings | undefined = prArtboardLayout
    ? {
        columnWidth: prArtboardLayout.columnWidth,
        drawHorizontal: prArtboardLayout.drawHorizontal,
        drawHorizontalLines: prArtboardLayout.drawHorizontalLines,
        drawVertical: prArtboardLayout.drawVertical,
        gutterHeight: prArtboardLayout.gutterHeight,
        gutterWidth: prArtboardLayout.gutterWidth,
        guttersOutside: prArtboardLayout.gutterOutside,
        horizontalOffset: prArtboardLayout.horizontalOffset,
        isEnabled: prArtboardLayout.isEnabled,
        numberOfColumns: prArtboardLayout.columnsCount,
        numberOfRows: 0,
        rowHeightMultiplication: prArtboardLayout.rowHeightMultiplication,
        totalWidth: prArtboardLayout.totalWidth,
      }
    : undefined

  return {
    ...defaultLayoutSettings,
    ...layoutSettings,
  }
}
